import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useUrlRedirectMutations } from '~/hooks/use-url-redirects';
import { urlRedirectSchema, EMPTY_URL_REDIRECT, type UrlRedirectFormValues } from '~/lib/schemas/url-redirect';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: UrlRedirectFormValues | null;
}

/** Add / edit a 301 URL redirect (from → to). */
export function UrlRedirectEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useUrlRedirectMutations();
	const form = useForm<UrlRedirectFormValues>({
		resolver: zodResolver(urlRedirectSchema) as Resolver<UrlRedirectFormValues>,
		defaultValues: initial ?? EMPTY_URL_REDIRECT,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_URL_REDIRECT);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.urlRedirects.editTitle') : t('system.urlRedirects.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.urlRedirects.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('system.urlRedirects.columns.from')} error={formState.errors.urlf?.message}>
						<Input {...register('urlf')} maxLength={400} aria-invalid={Boolean(formState.errors.urlf)} autoFocus />
					</FormField>
					<FormField label={t('system.urlRedirects.columns.to')} error={formState.errors.urlt?.message}>
						<Input {...register('urlt')} maxLength={400} aria-invalid={Boolean(formState.errors.urlt)} />
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
